<template>

<form id="userForm" class="mx-auto col-12" @submit.prevent>
  <div class="row">
    <div class="form-group col-lg-6">
      <label for="garage_accredited">{{ $t('garage_accredited') }}</label>
      <Combobox
        class="bg-white"
        name='garage_accredited'
        :placeholder="$t('select_a_item')"
        :hasErrors="errors.has('garage_accredited')"
        :options="garages"
        :initial-value="bankaccount.garageAccredited"
        label="fantasyName"
        v-model="bankaccount.garageAccredited"
        :reduce="garage => garage.id"
        v-validate="'required'"
      />
      <small v-show="errors.has('garage_accredited')" class="form-text text-muted">
        {{ errors.first('garage_accredited') }}
      </small>
    </div>

    <div class="form-group col-lg-6">
      <label for="bank">{{ $t('bank') }}</label>
        <Combobox
          class="bg-white"
          name='bank'
          :hasErrors="errors.has('bank')"
          :placeholder="$t('select_a_item')"
          :options="banks"
          label='name'
          :reduce="bank => bank.id"
          :initial-value='bankaccount.bank'
          v-model="bankaccount.bank"
          v-validate="'required'"
        />

      <small v-show="errors.has('bank')" class="form-text text-muted">
        {{ errors.first('bank') }}
      </small>
    </div>
  </div>

  <div class="row">
    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('agency') }">
      <label for="agency">{{ $t('agency') }}</label>
      <input
        type="text"
        id="agency"
        name="agency"
        class="form-control"
        :placeholder="$t('ph_agency')"
        v-model="bankaccount.agency"
      >
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('account') }">
      <label for="account">{{ $t('account') }}</label>
      <input
        type="text"
        id="account"
        name="account"
        class="form-control"
        :placeholder="$t('account')"
        v-model="bankaccount.account"
      >
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('pix') }">
      <label for="pix">{{ $t('pix') }}</label>
      <input
        type="text"
        id="pix"
        name="pix"
        class="form-control"
        :placeholder="$t('ph_pix')"
        v-model="bankaccount.pix"
      >
    </div>
  </div>

  <div class="row">
    <div class="form-group col-lg-4">
      <label for="type_account">{{ $t('type_account') }}</label>
        <Combobox
          class="bg-white"
          name='type_account'
          :hasErrors="errors.has('type_account')"
          :placeholder="$t('select_a_item')"
          :options="['Corrente', 'Poupança']"
          :initial-value='bankaccount.type'
          v-model="bankaccount.type"
          v-validate="'required'"
        />

      <small v-show="errors.has('type_account')" class="form-text text-muted">
        {{ errors.first('type_account') }}
      </small>
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('name') }">
      <label for="name">{{ $t('name') }}</label>
      <input
        type="text"
        id="name"
        name="name"
        class="form-control"
        :placeholder="$t('type_name')"
        v-model="bankaccount.name"
        v-validate="'required'"
        @input="upper('name')"
      />
      <small v-show="errors.has('name')" class="form-text text-muted">
        {{ errors.first('name') }}
      </small>
    </div>

    <CpfCnpjInput
      v-model="bankaccount.cpfCnpj"
      :type.sync="documentType"
      class="col-lg-4"
    />
  </div>

  <div class="form-group row">
    <div class="col-sm-4 col-sm-offset-2">
      <LoaderButton @click="onClick">
        {{ $t('save') }}
      </LoaderButton>
    </div>
  </div>
</form>

</template>

<script>
import { mapState, mapActions } from 'vuex'
import LoaderButton from '@/components/button/LoaderButton'
import Combobox from '@/components/shared/ComboBox'
import CpfCnpjInput from '@/components/shared/CpfCnpjInput.vue';

export default {
  name: 'BankAccountForm',
  components: {
    LoaderButton,
    Combobox,
    CpfCnpjInput
  },

  data() {
    return {
      documentType: 'cpf'
    }
  },

  props: {
    bankaccount: {
      type: Object,
      default: () => ({
        id              : undefined,
        garageAccredited: undefined,
        bank            : undefined,
        agency          : '',
        account         : '',
        pix             : '',
        type            : '',
        name            : undefined,
        cpfCnpj         : ''
      })
    }
  },

  watch: {
    'bankaccount.cpfCnpj': {
      immediate: true,
      handler(value) {
        if (!value) return

        const doc = String(value).replace(/[^A-Za-z0-9]/g, '')

        this.documentType = doc.length === 14 ? 'cnpj' : 'cpf'
      }
    }
  },

  computed: {
    ...mapState({
      bankaccounts: state => state.bankaccount.all,
      garages: state => state.garageaccredited.all,
      banks: state => state.banks.all
    })
  },

  methods: {
    ...mapActions('bankaccount', [
      'create',
      'edit',
      'cleanState'
    ]),

    customLabel (option) {
      return option.name
    },

    async onClick (evtClick, doneFn) {
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        const err = new Error(this.$t('invalid_form_fields'))
        this.$store.dispatch('notify/error', err)
        return
      }

      const {
        id,
        agency,
        account,
        pix
      } = this.bankaccount

      if (!agency && !account && !pix) {
        return this.$store.dispatch('notify/error', { message: 'noAgencyAndAccountOrPix' })
      }

      const promise = id
        ? this.edit({ id, data: this.bankaccount })
        : this.create(this.bankaccount)

      const result = await promise

      if (result) {
        this.$router.push({ name: 'listbankaccounts' })
      }
    },

    upper(attribute) {
      this.bankaccount[attribute] = this.bankaccount[attribute].toUpperCase()
    }
  },

  async mounted () {
    const { dispatch } = this.$store

    await dispatch('users/fetchPartners')
    await dispatch('garageaccredited/fetchAll')
    await dispatch('banks/fetchAll')

    if (this.$route.params.id) {
      this.bankaccount.bank = this.bankaccount.bank.id
    }
  },

  destroyed () {
    this.$store.dispatch('bankaccount/cleanState')
    this.$store.dispatch('banks/cleanState')
    this.cleanState()
  }
}
</script>
